import { useState } from "react";
import dayjs from "dayjs";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ConfirmModal from "../ConfirmModal";
import { pushGlobalError } from "../../errors/globalError";
import { formatNumber } from "../../utils";
import { useLeavePageGuard } from "../../utils/hooks/useLeavePageGuard";
import AdminExpenseFields from "./AdminExpenseFields";
import DayBanners from "./DayBanners";
import DayFormFooter from "./DayFormFooter";
import DayHeader from "./DayHeader";
import ExpenseHistory from "./ExpenseHistory";
import OtherExpenseRows from "./OtherExpenseRows";
import SalesExpenseTable from "./SalesExpenseTable";
import SkeletonCard from "./SkeletonCard";
import StateCard from "./StateCard";
import { API_MONTH_FORMAT, EMPTY_CELL_TEXT } from "./constants";
import { DailyExpenseFormErrors, buildSaveLines } from "./dailyExpenseForm";
import { neighbourDatesOf } from "./dailyExpenseMonth";
import { chipKindOf } from "./dailyExpenseStatus";
import { formatBusinessDate, formatTimestamp } from "./format";
import { useDailyExpenseForm } from "./useDailyExpenseForm";
import { useDailyExpenseWrites } from "./useDailyExpenseWrites";
import {
  useCategoryLayout,
  useDailyExpenseDay,
  useDailyExpenseMonth,
  useDayKey,
} from "./useDailyExpenseData";

/** The server's own answer to a plain submit of a zero day (DAILY_EXPENSE_NOTHING_TO_SUBMIT). */
const NOTHING_TO_SUBMIT_TEXT =
  "เอกสารนี้ยังไม่มียอดค่าใช้จ่าย หากวันนี้ไม่มีค่าใช้จ่ายให้กดปุ่ม “ไม่มีค่าใช้จ่ายวันนี้”";
/** What the form outlines before the user has tried to save: nothing. */
const NO_ERRORS: DailyExpenseFormErrors = {
  cells: new Set<string>(),
  admin: new Set<string>(),
  others: {},
  count: 0,
};

interface DailyExpenseDayFormProps {
  businessDate: string;
}

/**
 * S2 ค่าใช้จ่ายรายวัน, one day as a full page (the route table is too wide for a drawer):
 * ฝ่ายขาย per route with the แจ้งยอดโอน prefill, ฝ่ายบริหาร, อื่นๆ, the history, and the pinned
 * totals bar with บันทึกร่าง / ส่งให้ HO / ไม่มีค่าใช้จ่ายวันนี้. The buttons follow the server's
 * `actions`; a day the branch no longer holds is shown read-only.
 */
export default function DailyExpenseDayForm({ businessDate }: Readonly<DailyExpenseDayFormProps>) {
  const month = dayjs(businessDate).format(API_MONTH_FORMAT);
  const categories = useCategoryLayout();
  const dayAnswer = useDailyExpenseDay(useDayKey(businessDate));
  const monthAnswer = useDailyExpenseMonth(month);
  const day = dayAnswer.data;
  const layout = categories.data;
  const formState = useDailyExpenseForm(day, layout);
  const { form, errors, totals, showErrors, setShowErrors } = formState;
  const writes = useDailyExpenseWrites(businessDate, dayAnswer.replace);
  const leaveGuard = useLeavePageGuard(formState.isDirty);
  const [isConfirmingNoExpense, setConfirmingNoExpense] = useState(false);

  const neighbours = neighbourDatesOf(monthAnswer.data?.rows ?? [], businessDate);
  const isFailed = dayAnswer.isFailed || categories.isFailed;
  const canEdit = Boolean(day?.actions.can_edit);
  const isNoExpenseView = Boolean(day?.document?.no_expense) && !canEdit;
  const visibleErrors = showErrors && errors ? errors : NO_ERRORS;

  const retry = () => {
    if (dayAnswer.isFailed) dayAnswer.reload();
    if (categories.isFailed) categories.reload();
  };

  /** The lines to send, or null after flagging the fields that stop the save. */
  const linesIfValid = () => {
    if (!form || !errors || !layout) return null;
    if (errors.count > 0) {
      setShowErrors(true);
      return null;
    }
    setShowErrors(false);
    return buildSaveLines(form, layout);
  };
  const handleSaveDraft = () => {
    const lines = linesIfValid();
    if (lines) writes.saveDraft(lines);
  };
  const handleSubmit = () => {
    const lines = linesIfValid();
    if (!lines) return;
    if (!totals || totals.total === 0) {
      pushGlobalError(NOTHING_TO_SUBMIT_TEXT);
      return;
    }
    writes.submit(lines);
  };
  const openNoExpenseConfirm = () => setConfirmingNoExpense(true);
  const closeNoExpenseConfirm = () => setConfirmingNoExpense(false);
  const confirmNoExpense = async () => {
    await writes.declareNoExpense();
    setConfirmingNoExpense(false);
  };

  const summary = day
    ? {
        branchCode: day.branch_code,
        routeCount: day.routes.length,
        chipKind: chipKindOf(day.document?.status ?? null, day.is_locked),
      }
    : null;

  return (
    <Stack sx={{ py: 1 }} gap={2}>
      <DayHeader businessDate={businessDate} month={month} summary={summary} neighbours={neighbours} />

      {isFailed && (
        <StateCard
          title="โหลดเอกสารค่าใช้จ่ายไม่สำเร็จ"
          text="เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ กรุณาลองใหม่อีกครั้ง"
          onRetry={retry}
        />
      )}
      {!isFailed && (!day || !layout || !form || !totals) && <SkeletonCard />}

      {!isFailed && day && layout && form && totals && (
        <>
          <DayBanners day={day} errorCount={visibleErrors.count} />
          {isNoExpenseView ? (
            <Paper>
              <Stack alignItems="center" gap={0.75} sx={{ py: 4, textAlign: "center" }}>
                <Typography fontWeight={600}>ไม่มีค่าใช้จ่ายวันนี้</Typography>
                <Typography variant="body2" color="text.secondary">
                  สาขาส่งยอด {formatNumber(0)} บาท ให้ HO เมื่อ{" "}
                  {formatTimestamp(day.document?.submitted_at ?? null)} โดย{" "}
                  {day.document?.submitted_by_name ?? EMPTY_CELL_TEXT}
                </Typography>
              </Stack>
            </Paper>
          ) : (
            <>
              <SalesExpenseTable
                routes={day.routes}
                columns={layout.salesColumns}
                sales={form.sales}
                totals={totals}
                errorCells={visibleErrors.cells}
                readOnly={!canEdit}
                onChangeCell={formState.changeCell}
                onBlurCell={formState.tidyCell}
              />
              <AdminExpenseFields
                fields={layout.adminFields}
                values={form.admin}
                errorCodes={visibleErrors.admin}
                readOnly={!canEdit}
                onChange={formState.changeAdmin}
                onBlur={formState.tidyAdmin}
              />
              <OtherExpenseRows
                rows={form.others}
                errors={visibleErrors.others}
                routes={day.routes}
                readOnly={!canEdit}
                onAdd={formState.addOther}
                onChange={formState.changeOther}
                onBlurAmount={formState.tidyOtherAmount}
                onRemove={formState.removeOther}
              />
            </>
          )}
          <Paper>
            <ExpenseHistory history={day.history} collapsible />
          </Paper>
          <DayFormFooter
            salesTotal={totals.salesTotal}
            adminTotal={totals.adminTotal}
            total={totals.total}
            canEdit={canEdit}
            pendingWrite={writes.pendingWrite}
            onNoExpense={openNoExpenseConfirm}
            onSaveDraft={handleSaveDraft}
            onSubmit={handleSubmit}
          />
          <ConfirmModal
            open={isConfirmingNoExpense}
            title="ยืนยันว่าไม่มีค่าใช้จ่ายวันนี้"
            description={`ส่งเอกสารวันที่ ${formatBusinessDate(businessDate)} ของสาขา ${day.branch_code} ให้ HO ด้วยยอด ${formatNumber(0)} บาท ค่าที่กรอกไว้ในหน้านี้จะถูกล้าง`}
            confirmLabel="ยืนยันส่งยอด 0"
            loading={writes.pendingWrite === "NO_EXPENSE"}
            onClose={closeNoExpenseConfirm}
            onConfirm={confirmNoExpense}
          />
        </>
      )}

      <ConfirmModal
        open={leaveGuard.isAskingToLeave}
        title="ออกจากหน้านี้โดยไม่บันทึก?"
        description="ค่าที่กรอกไว้ในวันนี้ยังไม่ได้บันทึกร่าง ถ้าออกตอนนี้ค่าที่แก้จะหายไป"
        cancelLabel="อยู่ต่อ"
        confirmLabel="ออกโดยไม่บันทึก"
        confirmColor="error"
        onClose={leaveGuard.stay}
        onConfirm={leaveGuard.leave}
      />
    </Stack>
  );
}
